import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Plus, Save, Trash2, Edit3, X, Handshake, ExternalLink, ArrowUp, ArrowDown, Upload, ImageIcon, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIE = [
  { value: "tutti", label: "Tutti (mostrato in tutte le sezioni)" },
  { value: "privato", label: "Privato" },
  { value: "professionista", label: "Professionista" },
  { value: "azienda", label: "Azienda" },
];

const EMPTY = {
  nome: "",
  logo_url: "",
  sito_url: "",
  descrizione: "",
  categoria: "tutti",
  ordine: 0,
  attivo: true,
};

export default function PartnersAdminSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // record | "new" | null
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [tab, setTab] = useState("attivi"); // attivi | candidature
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/api/admin/partners`, { withCredentials: true })
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm({ ...EMPTY });
    setEditing("new");
  };

  const openEdit = (p) => {
    setForm({
      nome: p.nome || "",
      logo_url: p.logo_url || "",
      sito_url: p.sito_url || "",
      descrizione: p.descrizione || "",
      categoria: p.categoria || "tutti",
      ordine: p.ordine ?? 0,
      attivo: p.attivo !== false,
    });
    setEditing(p);
  };

  const closeEditor = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.nome || form.nome.trim().length < 2) {
      toast({ description: "Il nome è obbligatorio.", variant: "destructive" });
      return;
    }
    try {
      if (editing === "new") {
        await axios.post(`${API}/api/admin/partners`, form, { withCredentials: true });
        toast({ description: "Partner creato." });
      } else {
        await axios.put(`${API}/api/admin/partners/${editing.id}`, form, {
          withCredentials: true,
        });
        toast({ description: "Partner aggiornato." });
      }
      closeEditor();
      load();
    } catch (e) {
      toast({
        description: "Errore: " + (e.response?.data?.detail || e.message),
        variant: "destructive",
      });
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare "${p.nome}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/partners/${p.id}`, { withCredentials: true });
      toast({ description: "Partner eliminato." });
      load();
    } catch (e) {
      toast({
        description: "Errore: " + (e.response?.data?.detail || e.message),
        variant: "destructive",
      });
    }
  };

  const toggleAttivo = async (p) => {
    try {
      await axios.put(
        `${API}/api/admin/partners/${p.id}`,
        { attivo: !p.attivo },
        { withCredentials: true }
      );
      load();
    } catch (e) {
      toast({
        description: "Errore: " + (e.response?.data?.detail || e.message),
        variant: "destructive",
      });
    }
  };

  const moveOrdine = async (p, delta) => {
    try {
      await axios.put(
        `${API}/api/admin/partners/${p.id}`,
        { ordine: (p.ordine || 0) + delta },
        { withCredentials: true }
      );
      load();
    } catch (e) {
      // silent
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ALLOWED = ["image/jpeg", "image/png", "image/webp"];
    if (!ALLOWED.includes(file.type)) {
      toast({
        description: "Formato non supportato. Usa JPG, PNG o WEBP.",
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }
    const MAX_MB = 2;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast({
        description: `Logo troppo grande (max ${MAX_MB} MB).`,
        variant: "destructive",
      });
      e.target.value = "";
      return;
    }

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/api/admin/upload?tipo=immagine`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.url;
      if (!url) throw new Error("URL logo mancante nella risposta");
      // API restituisce /api/files/{id} — prefissiamo con API URL così è assoluto
      const absoluteUrl = url.startsWith("http") ? url : `${API}${url}`;
      setForm((f) => ({ ...f, logo_url: absoluteUrl }));
      toast({ description: "Logo caricato con successo." });
    } catch (err) {
      toast({
        description: "Errore upload: " + (err.response?.data?.detail || err.message),
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (editing) {
    return (
      <div className="space-y-6" data-testid="partners-editor">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Handshake className="h-6 w-6 text-[#f59e0b]" />
            <h2 className="text-2xl font-extrabold">
              {editing === "new" ? "Nuovo Partner" : `Modifica: ${editing.nome}`}
            </h2>
          </div>
          <Button
            onClick={closeEditor}
            variant="outline"
            className="border-white/20 text-slate-200 hover:bg-white/10 hover:text-white"
            data-testid="partners-editor-close"
          >
            <X className="h-4 w-4 mr-1" /> Chiudi
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-5 p-6 rounded-2xl bg-[#0a1020] border border-white/5">
          <div>
            <Label className="text-slate-300">Nome partner *</Label>
            <Input
              data-testid="partners-form-nome"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              className="bg-[#0e1424] border-white/10 text-white mt-1"
              placeholder="Es. Esse Studi"
            />
          </div>
          <div>
            <Label className="text-slate-300">Categoria</Label>
            <select
              data-testid="partners-form-categoria"
              value={form.categoria}
              onChange={(e) => setForm({ ...form, categoria: e.target.value })}
              className="w-full mt-1 h-10 px-3 rounded-md bg-[#0e1424] border border-white/10 text-white text-sm"
            >
              {CATEGORIE.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-slate-300">Logo del partner</Label>
            <div className="mt-2 flex items-start gap-4">
              {/* Preview */}
              <div className="h-24 w-24 flex-shrink-0 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden">
                {form.logo_url ? (
                  <img
                    src={form.logo_url.startsWith("http") ? form.logo_url : `${API}${form.logo_url}`}
                    alt="Preview logo"
                    className="max-h-20 max-w-20 object-contain"
                    data-testid="partners-form-logo-preview"
                  />
                ) : (
                  <ImageIcon className="h-8 w-8 text-slate-500" />
                )}
              </div>
              {/* Upload + URL */}
              <div className="flex-1 space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                  data-testid="partners-form-logo-file"
                />
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-bold h-9"
                    data-testid="partners-form-logo-upload-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        Caricamento…
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-1" />
                        Carica logo (PC)
                      </>
                    )}
                  </Button>
                  {form.logo_url && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setForm({ ...form, logo_url: "" })}
                      className="h-9 border-white/20 text-slate-200 hover:bg-white/10 hover:text-white"
                      data-testid="partners-form-logo-remove"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Rimuovi
                    </Button>
                  )}
                </div>
                <div className="text-[11px] text-slate-500">
                  Formati: JPG, PNG, WEBP · max 2 MB. In alternativa, incolla un URL:
                </div>
                <Input
                  data-testid="partners-form-logo"
                  value={form.logo_url}
                  onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                  className="bg-[#0e1424] border-white/10 text-white"
                  placeholder="https://... oppure lascia vuoto"
                />
              </div>
            </div>
          </div>
          <div>
            <Label className="text-slate-300">Sito web</Label>
            <Input
              data-testid="partners-form-sito"
              value={form.sito_url}
              onChange={(e) => setForm({ ...form, sito_url: e.target.value })}
              className="bg-[#0e1424] border-white/10 text-white mt-1"
              placeholder="https://esempio.com"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-slate-300">Descrizione breve</Label>
            <Textarea
              data-testid="partners-form-desc"
              value={form.descrizione}
              onChange={(e) => setForm({ ...form, descrizione: e.target.value })}
              className="bg-[#0e1424] border-white/10 text-white mt-1 min-h-[80px]"
              placeholder="Ente di formazione partner..."
            />
          </div>
          <div>
            <Label className="text-slate-300">Ordine di visualizzazione</Label>
            <Input
              data-testid="partners-form-ordine"
              type="number"
              value={form.ordine}
              onChange={(e) => setForm({ ...form, ordine: parseInt(e.target.value || "0", 10) })}
              className="bg-[#0e1424] border-white/10 text-white mt-1"
            />
          </div>
          <div className="flex items-center gap-3 mt-6">
            <label className="flex items-center gap-2 text-slate-200 cursor-pointer">
              <input
                data-testid="partners-form-attivo"
                type="checkbox"
                checked={form.attivo}
                onChange={(e) => setForm({ ...form, attivo: e.target.checked })}
              />
              Partner attivo (visibile pubblicamente)
            </label>
          </div>

          <div className="md:col-span-2 flex justify-end pt-2">
            <Button
              data-testid="partners-editor-save"
              onClick={save}
              className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-bold"
            >
              <Save className="h-4 w-4 mr-1" />
              Salva
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="partners-admin">
      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-white/10 pb-3">
        <button
          onClick={() => setTab("attivi")}
          data-testid="partners-tab-attivi"
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
            tab === "attivi"
              ? "bg-[#f59e0b] text-[#0a1020]"
              : "text-slate-300 hover:bg-white/5"
          }`}
        >
          <Handshake className="h-4 w-4 inline mr-1.5" />
          Partner attivi
        </button>
        <button
          onClick={() => setTab("candidature")}
          data-testid="partners-tab-candidature"
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
            tab === "candidature"
              ? "bg-[#f59e0b] text-[#0a1020]"
              : "text-slate-300 hover:bg-white/5"
          }`}
        >
          Candidature ricevute
        </button>
      </div>

      {tab === "candidature" ? (
        <CandidaturePartnerAdmin />
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Handshake className="h-6 w-6 text-[#f59e0b]" />
              <div>
                <h2 className="text-2xl font-extrabold">Partner & Opportunità Extra</h2>
                <div className="text-xs text-slate-400">
                  Gestisci le aziende partner mostrate nella pagina{" "}
                  <span className="text-[#f59e0b] font-semibold">/opportunita-extra</span>
                </div>
              </div>
            </div>
            <Button
              data-testid="partners-btn-new"
              onClick={openNew}
              className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-bold"
            >
              <Plus className="h-4 w-4 mr-1" />
              Nuovo Partner
            </Button>
          </div>

      {loading ? (
        <div className="text-slate-400">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="p-10 rounded-2xl bg-[#0a1020] border border-white/5 text-center text-slate-300">
          Nessun partner registrato. Clicca &quot;Nuovo Partner&quot; per iniziare.
        </div>
      ) : (
        <div className="rounded-2xl bg-[#0a1020] border border-white/5 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Ord.</th>
                <th className="text-left px-4 py-3">Logo</th>
                <th className="text-left px-4 py-3">Nome</th>
                <th className="text-left px-4 py-3">Categoria</th>
                <th className="text-left px-4 py-3">Sito</th>
                <th className="text-left px-4 py-3">Stato</th>
                <th className="text-right px-4 py-3">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {items.map((p) => (
                <tr key={p.id} data-testid={`partners-row-${p.id}`} className="hover:bg-white/[0.02]">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => moveOrdine(p, -1)}
                        className="p-1 hover:bg-white/10 rounded"
                        data-testid={`partners-up-${p.id}`}
                      >
                        <ArrowUp className="h-3.5 w-3.5 text-slate-400" />
                      </button>
                      <span className="font-mono text-slate-300 w-6 text-center">{p.ordine ?? 0}</span>
                      <button
                        onClick={() => moveOrdine(p, 1)}
                        className="p-1 hover:bg-white/10 rounded"
                        data-testid={`partners-down-${p.id}`}
                      >
                        <ArrowDown className="h-3.5 w-3.5 text-slate-400" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="h-10 w-10 rounded-lg bg-white/5 flex items-center justify-center overflow-hidden">
                      {p.logo_url ? (
                        <img
                          src={p.logo_url.startsWith("http") ? p.logo_url : `${API}${p.logo_url}`}
                          alt=""
                          className="max-h-9 max-w-full object-contain"
                        />
                      ) : (
                        <span className="text-slate-400 text-xs">n/a</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-white font-semibold">{p.nome}</td>
                  <td className="px-4 py-3">
                    <span className="inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#f59e0b]/10 text-[#f59e0b] font-bold">
                      {p.categoria}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {p.sito_url ? (
                      <a
                        href={p.sito_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-slate-300 hover:text-white"
                      >
                        <ExternalLink className="h-3.5 w-3.5" /> Apri
                      </a>
                    ) : (
                      <span className="text-slate-500">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleAttivo(p)}
                      data-testid={`partners-toggle-${p.id}`}
                      className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        p.attivo
                          ? "bg-emerald-500/15 text-emerald-400"
                          : "bg-slate-500/15 text-slate-400"
                      }`}
                    >
                      {p.attivo ? "Attivo" : "Nascosto"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(p)}
                        data-testid={`partners-edit-${p.id}`}
                        className="p-2 rounded-lg hover:bg-white/10 text-slate-300 hover:text-white"
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => remove(p)}
                        data-testid={`partners-delete-${p.id}`}
                        className="p-2 rounded-lg hover:bg-red-500/20 text-red-400"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
        </>
      )}
    </div>
  );
}

// ============ Candidature Partner ADMIN ============
const STATI_CANDIDATURA = [
  { value: "nuova", label: "Nuova", color: "bg-blue-500/15 text-blue-300" },
  { value: "contattato", label: "Contattato", color: "bg-amber-500/15 text-amber-300" },
  { value: "approvato", label: "Approvato", color: "bg-emerald-500/15 text-emerald-300" },
  { value: "archiviato", label: "Archiviato", color: "bg-slate-500/15 text-slate-400" },
];

function CandidaturePartnerAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    const url = filter
      ? `${API}/api/admin/partners/candidature?stato=${filter}`
      : `${API}/api/admin/partners/candidature`;
    axios
      .get(url, { withCredentials: true })
      .then((r) => setList(r.data || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [filter]);

  const setStato = async (c, nuovo) => {
    try {
      await axios.patch(
        `${API}/api/admin/partners/candidature/${c.id}`,
        { stato: nuovo },
        { withCredentials: true }
      );
      toast({ description: `Stato aggiornato a: ${nuovo}` });
      load();
    } catch (e) {
      toast({
        description: "Errore: " + (e.response?.data?.detail || e.message),
        variant: "destructive",
      });
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Eliminare la candidatura di ${c.nome_azienda}?`)) return;
    try {
      await axios.delete(`${API}/api/admin/partners/candidature/${c.id}`, {
        withCredentials: true,
      });
      toast({ description: "Candidatura eliminata." });
      load();
    } catch (e) {
      toast({
        description: "Errore: " + (e.response?.data?.detail || e.message),
        variant: "destructive",
      });
    }
  };

  const statoInfo = (s) => STATI_CANDIDATURA.find((x) => x.value === s) || STATI_CANDIDATURA[0];

  return (
    <div className="space-y-4" data-testid="candidature-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-extrabold">Candidature Partner ricevute</h2>
          <div className="text-xs text-slate-400">
            Aziende che hanno inviato il form &quot;Diventa Partner ACDT&quot;
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="candidature-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-9 px-3 rounded-md bg-[#0e1424] border border-white/10 text-white text-sm"
          >
            <option value="">Tutti gli stati</option>
            {STATI_CANDIDATURA.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-slate-400">Caricamento…</div>
      ) : list.length === 0 ? (
        <div
          data-testid="candidature-empty"
          className="p-10 rounded-2xl bg-[#0a1020] border border-white/5 text-center text-slate-300"
        >
          Nessuna candidatura {filter ? `con stato "${filter}"` : "ricevuta"}.
        </div>
      ) : (
        <div className="rounded-2xl bg-[#0a1020] border border-white/5 divide-y divide-white/5">
          {list.map((c) => {
            const isOpen = expanded === c.id;
            const s = statoInfo(c.stato);
            return (
              <div key={c.id} data-testid={`candidatura-row-${c.id}`} className="p-4">
                <div
                  className="flex flex-wrap items-center justify-between gap-3 cursor-pointer"
                  onClick={() => setExpanded(isOpen ? null : c.id)}
                >
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-white font-bold text-base">{c.nome_azienda}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ${s.color}`}>
                        {s.label}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider bg-white/5 text-slate-400 px-2 py-0.5 rounded-full">
                        {c.tipo_attivita.replace("_", " ")}
                      </span>
                    </div>
                    <div className="text-[13px] text-slate-400 mt-1">
                      {c.referente} · {c.email} {c.telefono && `· ${c.telefono}`}
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 whitespace-nowrap">
                    {new Date(c.created_at).toLocaleString("it-IT")}
                  </div>
                </div>

                {isOpen && (
                  <div className="mt-4 pl-1 space-y-4">
                    {c.sito_web && (
                      <div className="text-sm text-slate-300">
                        <span className="text-slate-500">Sito:</span>{" "}
                        <a
                          href={c.sito_web}
                          target="_blank"
                          rel="noreferrer"
                          className="text-[#f59e0b] hover:underline"
                        >
                          {c.sito_web}
                        </a>
                      </div>
                    )}
                    {c.settore && (
                      <div className="text-sm text-slate-300">
                        <span className="text-slate-500">Settore:</span> {c.settore}
                      </div>
                    )}
                    <div className="p-3 rounded-lg bg-white/5 text-slate-200 text-sm whitespace-pre-wrap">
                      {c.messaggio}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pt-2">
                      {STATI_CANDIDATURA.filter((x) => x.value !== c.stato).map((x) => (
                        <button
                          key={x.value}
                          onClick={() => setStato(c, x.value)}
                          data-testid={`candidatura-set-${x.value}-${c.id}`}
                          className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${x.color} hover:opacity-80`}
                        >
                          Segna come {x.label}
                        </button>
                      ))}
                      <a
                        href={`mailto:${c.email}?subject=Partnership%20ACDT%20-%20${encodeURIComponent(c.nome_azienda)}`}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#f59e0b] text-[#0a1020] hover:bg-[#d97706]"
                        data-testid={`candidatura-reply-${c.id}`}
                      >
                        Rispondi via email
                      </a>
                      <button
                        onClick={() => remove(c)}
                        data-testid={`candidatura-delete-${c.id}`}
                        className="ml-auto px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-500/15 text-red-400 hover:bg-red-500/25"
                      >
                        <Trash2 className="h-3.5 w-3.5 inline mr-1" />
                        Elimina
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
